import { useEffect, useRef, useState } from "react";

import type { Client, MatrixMediaBytes, MatrixMediaSource } from "~/lib/matrix";

export interface UseMediaObjectUrlOptions {
  enabled?: boolean;
  thumbnail?: { w: number; h: number; method: "scale" | "crop" };
}

export type UseMediaObjectUrlResult =
  | { status: "loading" }
  | { status: "ready"; src: string }
  | { status: "failed" };

export function useMediaObjectUrl(
  client: Client,
  source: MatrixMediaSource,
  mimetype?: string,
  opts?: UseMediaObjectUrlOptions,
): UseMediaObjectUrlResult {
  const [state, setState] = useState<UseMediaObjectUrlResult>(() =>
    "url" in source && source.url.startsWith("blob:")
      ? { status: "ready", src: source.url }
      : { status: "loading" },
  );
  const thumbnail = opts?.thumbnail;
  const thumbnailW = thumbnail?.w;
  const thumbnailH = thumbnail?.h;
  const thumbnailMethod = thumbnail?.method;
  const sourceKey = mediaSourceKey(source);
  const stableSource = useStableMediaSource(source, sourceKey);
  const enabled = opts?.enabled ?? true;
  const localSrcRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    if (!enabled) {
      setState({ status: "loading" });
      return () => {
        cancelled = true;
      };
    }
    const downloadOpts =
      thumbnailW === undefined || thumbnailH === undefined || thumbnailMethod === undefined
        ? undefined
        : { thumbnail: { w: thumbnailW, h: thumbnailH, method: thumbnailMethod } };
    if ("url" in stableSource && stableSource.url.startsWith("blob:")) {
      localSrcRef.current = stableSource.url;
      setState({ status: "ready", src: stableSource.url });
      return () => {
        cancelled = true;
      };
    }
    const localSrc = localSrcRef.current;
    localSrcRef.current = null;
    setState((prev) =>
      prev.status === "ready" && prev.src === localSrc ? prev : { status: "loading" },
    );
    void (async () => {
      try {
        const media = await client.bindings.downloadMedia(stableSource, downloadOpts);
        objectUrl = objectUrlFromBytes(media.bytes, mimetype);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setState({ status: "ready", src: objectUrl });
      } catch (err) {
        console.warn("Matrix media download failed", err);
        if (!cancelled) setState({ status: "failed" });
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [client, enabled, mimetype, stableSource, thumbnailH, thumbnailMethod, thumbnailW]);

  return state;
}

export function useStableMediaSource(source: MatrixMediaSource, key: string): MatrixMediaSource {
  const ref = useRef<{ key: string; source: MatrixMediaSource }>({ key, source });
  if (ref.current.key !== key) ref.current = { key, source };
  return ref.current.source;
}

export function objectUrlFromBytes(bytes: MatrixMediaBytes["bytes"], mimetype?: string): string {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return URL.createObjectURL(new Blob([buffer], { type: mimetype || "application/octet-stream" }));
}

export function mediaSourceKey(source: MatrixMediaSource): string {
  if ("url" in source) return source.url;
  try {
    return JSON.stringify(source.file);
  } catch {
    return "encrypted-media";
  }
}
